import { b64urlEncode, b64encode } from "./util/base64"
import { Session } from "./hpke"
import type { AttestBundle, EncryptedResponse, AttestOptions, OpenedSession } from "./types"
import type { CSOidcPayload } from "./oidc"
import { WORKER_TIMEOUT_MS } from "./constants"

// ── Public API re-exports ─────────────────────────────────────────────────────

// Core types
export type { AttestBundle, EncryptedRequest, EncryptedResponse, AttestOptions, OpenedSession } from "./types"
export type { CSOidcPayload } from "./oidc"

// Bundle verification (low-level — use attestAndOpen for the common case)
export { verifyBundle } from "./bundle"
export type { OIDCVerifyFn, VerifiedSession } from "./bundle"

// HPKE session (low-level — for custom encryption flows)
export { Session, uploadChunked } from "./hpke"

// ONNX model transfer (high-level convenience)
export { transferOnnxModel, submitOnnxModel, fetchJobResults } from "./transfer-onnx"
export type {
  TransferOptions,
  TransferResult,
  TransferPhase,
  TransferCheckpoint,
  StepName,
  SubmitOptions,
  SubmitResult,
  UploadStepName,
  JobEvaluationResult,
  JobEvaluationAttestation,
} from "./transfer-onnx"

// Encrypted, chunked dataset upload (client-side envelope encryption, KMS-wrapped DEK)
export {
  computeFileSha256,
  importWrappingKey,
  encryptedPartCount,
  encryptAndUploadChunks,
  manifestKeyFor,
  DATASET_CHUNK_SIZE,
  ENC_DATASET_SCHEMA,
} from "./encrypted-dataset-upload"
export type { EncryptedDatasetManifest, EncryptChunksOptions } from "./encrypted-dataset-upload"

// Debug OIDC verifier — works with dbgstat=enabled enclaves; NEVER use in production
export { verifyOIDCDebug } from "./oidc_debug"

// Selected constants
export { EXPECTED_IMAGE_DIGEST, OIDC_AUDIENCE, OIDC_ISSUER, BUFFER_TEE_SOURCE_REPO_URL } from "./constants"

// ── Internal ──────────────────────────────────────────────────────────────────

const CHUNK_SIZE = 16 * 1024 * 1024   // 16 MB per AES-GCM chunk

async function resolveKeycloakToken(opts: AttestOptions): Promise<string | undefined> {
    if (opts.getKeycloakToken) {
        return opts.getKeycloakToken()
    }
    return opts.keycloakToken
}

async function withAuthHeaders(
    opts: AttestOptions,
    headers: Record<string, string>,
): Promise<Record<string, string>> {
    const token = await resolveKeycloakToken(opts)
    if (token) {
        headers["Authorization"] = `Bearer ${token}`
    }
    return headers
}

// Join a TEE endpoint path onto the base URL, preserving any base path prefix
// (e.g. base "https://bodh.tanuh.ai/tee" + "/v1/submit" →
// "https://bodh.tanuh.ai/tee/v1/submit"). `new URL(path, base)` must not
// be used here: a leading-slash path discards the base's "/tee" prefix.
// When the base is empty the Vite proxy handles routing via relative paths.
function teeRequestUrl(base: string | undefined, path: string): string {
    if (!base) return path
    return base.replace(/\/+$/, "") + path
}

/** Status plus the response body, same shape as upload failures. */
async function failedRequestError(kind: string, resp: Response): Promise<Error> {
    const msg = (await resp.text().catch(() => "")).trim()
    return new Error(`${kind} failed: ${resp.status}${msg ? " — " + msg : ""}`)
}

type WorkerResponse =
    | { type: "verify_ok"; session: { hpkePub: ArrayBuffer; verifiedAt: number; oidc: CSOidcPayload } }
    | { type: "encrypt_ok"; request: import("./types").EncryptedRequest }
    | { type: "error"; message: string }

function runInWorker<T>(
    message: object,
    transferables: ArrayBuffer[],
): Promise<T> {
    return new Promise((resolve, reject) => {
        const worker = new Worker(new URL("./worker.worker", import.meta.url), { type: "module" })

        // Timeout: kill hung worker after 30s
        const timeout = setTimeout(() => {
            worker.terminate()
            reject(new Error(`Worker timeout after ${WORKER_TIMEOUT_MS / 1000}s`))
        }, WORKER_TIMEOUT_MS)

        worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
            clearTimeout(timeout)
            if (e.data.type === "error") {
                reject(new Error(e.data.message))
            } else {
                resolve(e.data as T)
            }
            // self.close() already called inside worker — no terminate needed here
        }

        worker.onerror = (e: ErrorEvent) => {
            clearTimeout(timeout)
            worker.terminate()
            reject(new Error(`Worker error: ${e.message ?? "unknown"}`))
        }

        worker.onmessageerror = () => {
            clearTimeout(timeout)
            worker.terminate()
            reject(new Error("Worker message serialization error"))
        }

        // Transfer: main thread loses access to these ArrayBuffers after this call
        worker.postMessage(message, transferables)
    })
}

export async function attestAndOpen(opts: AttestOptions): Promise<OpenedSession> {
    // 1. Generate fresh liveness nonce
    const nonce = crypto.getRandomValues(new Uint8Array(32))

    // 2. Fetch bundle — nonce goes in header as base64url (RawURLEncoding, no padding)
    const attestHeaders = await withAuthHeaders(opts, { "X-RATLS-Nonce": b64urlEncode(nonce) })
    const resp = await fetch(opts.attestUrl + "/v1/attest", { headers: attestHeaders })
    if (!resp.ok) throw await failedRequestError("attest", resp)
    const bundle = await resp.json() as AttestBundle

    // 3. Verify in worker — transfer nonce (main thread loses access)
    const { session } = await runInWorker<{
        session: { hpkePub: ArrayBuffer; verifiedAt: number; oidc: CSOidcPayload }
    }>(
        { type: "verify", bundle, nonce: nonce.buffer },
        [nonce.buffer],
    )

    // 4. Build HPKE session with verified enclave pub
    const enclaveHpkePub = new Uint8Array(session.hpkePub)
    const hpkeSession = new Session(enclaveHpkePub)
    await hpkeSession.init()

    return {
        hpkePub: enclaveHpkePub,
        oidc:    session.oidc,

        bearerToken(): Promise<string | undefined> {
            return resolveKeycloakToken(opts)
        },

        // Encrypt File using AES-256-GCM-CHUNKED-v1, 16 MB per chunk.
        // Encrypts in 16 MB pieces (peak: ~32 MB during encryption),
        // then sends the assembled body in one fetch — works through any proxy.
        // sha256hex must be the pre-computed hex SHA-256 of the plaintext file.
        async uploadEncrypted(path: string, file: File, sha256hex: string, onProgress?: (pct: number) => void): Promise<void> {
            const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE))
            const fileId      = crypto.randomUUID()

            const rawKey = crypto.getRandomValues(new Uint8Array(32))
            const aesKey = await crypto.subtle.importKey(
                "raw", rawKey, { name: "AES-GCM" }, false, ["encrypt"],
            )
            const { enc, wrappedKey } = await hpkeSession.sealSymmetricKey(rawKey)

            // 12-byte base IV; chunk i uses baseIV + i (96-bit big-endian increment).
            const baseIV = crypto.getRandomValues(new Uint8Array(12))

            const header      = JSON.stringify({
                file_id:          fileId,
                enc,
                wrapped_key:      wrappedKey,
                base_iv:          b64encode(baseIV),
                total_chunks:     totalChunks,
                plaintext_sha256: sha256hex,
            })
            const headerBytes = new TextEncoder().encode(header)

            // Pre-allocate the full body:
            //   4 bytes header-len + header + per-chunk (4 bytes len + plaintext + 16 GCM tag)
            const bodySize = 4 + headerBytes.length
                + totalChunks * 4
                + file.size + totalChunks * 16
            const body = new Uint8Array(bodySize)
            const dv   = new DataView(body.buffer)
            let   off  = 0

            // Write header frame
            dv.setUint32(off, headerBytes.length, false); off += 4
            body.set(headerBytes, off);                   off += headerBytes.length

            // Encrypt and write each chunk; IV starts at baseIV and increments by 1 each chunk
            const iv = new Uint8Array(baseIV)

            for (let i = 0; i < totalChunks; i++) {
                const plaintext = await file.slice(
                    i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE,
                ).arrayBuffer()

                const aad = new TextEncoder().encode(`${i}:${totalChunks}:${fileId}`)

                const ct = new Uint8Array(await crypto.subtle.encrypt(
                    { name: "AES-GCM", iv: iv.slice(), additionalData: aad, tagLength: 128 },
                    aesKey,
                    plaintext,
                ))

                dv.setUint32(off, ct.length, false); off += 4
                body.set(ct, off);                   off += ct.length

                onProgress?.(Math.round((i + 1) / totalChunks * 100))
                // increment IV by 1 (big-endian, with carry) for the next chunk
                for (let j = 11; j >= 0; j--) { if (++iv[j] !== 0) break }
            }

            const url = teeRequestUrl(opts.attestUrl, path)
            const uploadHeaders = await withAuthHeaders(opts, {
                "Content-Type":         "application/octet-stream",
                "X-RATLS-Browser-HPKE": hpkeSession.browserPubB64Url(),
            })
            const resp = await fetch(url, { method: "PUT", headers: uploadHeaders, body })
            if (!resp.ok) throw await failedRequestError("upload", resp)
        },

        async get(path: string): Promise<unknown | null> {
            const url = teeRequestUrl(opts.attestUrl, path)
            const getHeaders = await withAuthHeaders(opts, {
                "X-RATLS-Browser-HPKE": hpkeSession.browserPubB64Url(),
            })
            const r = await fetch(url, { method: "GET", headers: getHeaders })
            if (!r.ok) {
                return null
            }
            return r.json()
        },

        async send(path: string, body: unknown): Promise<unknown> {
            const plaintext = new TextEncoder().encode(JSON.stringify(body))
            const aad = new TextEncoder().encode(JSON.stringify({
                rid:  crypto.randomUUID(),
                ts:   Date.now(),
                path,
            }))

            // Encrypt with hpkeSession directly — the worker would create a NEW browser
            // ephemeral key, making the HPKE info mismatch the X-RATLS-Browser-HPKE header.
            // hpkeSession.encryptRequest() zeroes `plaintext` after sealing.
            const request = await hpkeSession.encryptRequest(plaintext, aad)

            // When attestUrl is empty the Vite proxy handles routing via relative paths.
            const submitUrl = teeRequestUrl(opts.attestUrl, path)
            const sendHeaders = await withAuthHeaders(opts, {
                "Content-Type":         "application/json",
                "X-RATLS-Browser-HPKE": hpkeSession.browserPubB64Url(),
            })
            const r = await fetch(submitUrl, {
                method:  "POST",
                headers: sendHeaders,
                body:    JSON.stringify(request),
            })
            if (!r.ok) throw await failedRequestError("submit", r)

            const er = await r.json() as EncryptedResponse
            const pt = await hpkeSession.decryptResponse(er)
            return JSON.parse(new TextDecoder().decode(pt))
        },
    }
}
